import startCase from 'lodash-es/startCase'
import { useEffect, useState } from 'react'
import Button from '#/components/Button'
import Checkbox from '#/components/Checkbox'
import { defaultSectionsConfig } from '#/containers/PDF/FullProperty/components/Report/config'
import type { SectionsConfig } from '#/containers/PDF/FullProperty/components/Report/types'
import { logEvent } from '#/services/analytics'
import { reportName } from '#/utils/report'
import UserBlockPreview from './UserBlockPreview'

/**
 * Customizing Report
 * - Currently this is for the Full Property Report only
 * - The user selects checkboxes indication which sections to include in the report
 * - This component only takes care of setting the config of the report not requesting it
 */

type ReportCustomizeProps = {
  property: TopHap.Property
  reportType: TopHap.ReportType
  user: TopHap.UserInfo
  onChangeConfig: (config:SectionsConfig) => void
}

const ReportCustomize: React.FC<ReportCustomizeProps> = ({
  reportType,
  property,
  user,
  onChangeConfig
}) => {
  const [config, setConfig] = useState<SectionsConfig>(defaultSectionsConfig)

  useEffect(() => {
    logEvent('select_content', {
      content_type: 'property_detail',
      item_id: `report_${reportType.toLowerCase()}_customize`

    })
  }, [reportType])

  // calls the callback most likely to request a report
  const handleDone = () => {
    config && onChangeConfig(config)
  }

  // resets the checkboxes to the default config of the report
  const handleReset = () => {
    setConfig(defaultSectionsConfig)
  }

  // updates config on checkbox manipulation
  const handleChangeConfig = (section: string) => {
    const update: any = {}
    update[`${section}`] = { enabled: !config[section as keyof SectionsConfig].enabled }

    setConfig({
      ...config,
      ...update
    })
  }

  const forceDownload = (url: string) => {
    fetch(url).then(t => t.blob().then(b => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(b)
      a.setAttribute('download', 'TopHap-Full-Property-Report-Sample')
      a.click()
    }))
  }

  return (
    <div>
      <h3 className='text-xl font-bold text-neutral-800'>{`Customize ${reportName(reportType)}`}</h3>
      <p className='my-2 text-sm'>
        <span>
          {property.displayAddress}, {property.displayRegion}
        </span>
        <br />
        <button
          className='text-primary underline hover:underline'
          onClick={() => forceDownload('https://assets.tophap.com/cms/TopHap-Full-Property-Report-Sample-1.pdf')}
        >
          Download Sample Report
        </button>
      </p>
      <UserBlockPreview user={user} />
      <ul className='list-none columns-1 gap-0.5 sm:columns-2'>
        {config && Object.keys(config).map(key => (
          <li key={key}>
            <Checkbox
              checked={config[key as keyof SectionsConfig].enabled}
              label={startCase(key)}
              onChange={() => handleChangeConfig(key)}
            />
          </li>
        ))}
      </ul>

      <div className='mt-4 flex justify-between'>
        <Button className='rounded p-1 text-sm text-primary underline' onClick={handleReset}>
          Reset
        </Button>
        <Button
          className='h-10 rounded-md bg-gradient-to-r from-primary to-gradient-blue px-3 text-sm text-white'
          onClick={handleDone}
        >
          Done
        </Button>
      </div>
    </div>
  )
}

export default ReportCustomize
